<template>
  <div v-if="!useVuetify" class="typeahead-container">
    <el-select
      v-model="internalValue"
      @change="doChange"
      filterable
      remote
      :remote-method="filterContacts"
      :loading="loading"
      :placeholder="placeholder"
      :size="size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'default'"
      :style="block ? 'display:block' : ''"
    >
      <el-option v-for="option in options" :key="option.id" :value="option.id">
        <span style="float: left">{{
          option.first_name + ' ' + option.last_name
        }}</span>
        <span
          v-if="option.primaryEmail"
          style="
            float: right;
            color: var(--el-text-color-secondary);
            font-size: 13px;
          "
        >
          {{ option.primaryEmail.email_address }}
        </span>
        <span
          v-if="option.mobile_phone"
          style="
            float: right;
            color: var(--el-text-color-primary);
            font-size: 13px;
            padding-right: 4px;
          "
        >
          {{ formatPhoneNumber(option.mobile_phone) }}
        </span>
      </el-option>
    </el-select>
  </div>
  <div v-else class="typeahead-container">
    <v-text-field
      v-model="searchQuery"
      :label="label"
      @input="onSearchInput"
      :density="
        size === 'small'
          ? 'compact'
          : size === 'large'
          ? 'comfortable'
          : 'default'
      "
      :loading="loading"
      :error="error"
      :error-messages="errorMessages"
      hide-details="auto"
    >
      <template v-slot:append-inner v-if="loading">
        <v-progress-circular
          indeterminate
          color="primary"
          size="20"
        ></v-progress-circular>
      </template>
    </v-text-field>

    <!-- Results dropdown -->
    <div v-if="showResults && options.length > 0" class="results-dropdown">
      <div
        v-for="option in options"
        :key="option.id"
        class="result-item"
        @click="selectContact(option)"
      >
        <div class="contact-name">
          {{ option.first_name + ' ' + option.last_name }}
        </div>
        <div v-if="option.primaryEmail" class="contact-email">
          {{ option.primaryEmail.email_address }}
        </div>
        <div v-if="option.mobile_phone" class="contact-phone">
          {{ formatPhoneNumber(option.mobile_phone) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import ContactService from 'src/services/contactServices.js';

export default {
  name: 'contactSelect',
  computed: {},
  props: {
    value: {
      type: [Number, String],
    },
    placeholder: {
      type: String,
      required: false,
      default: () => {
        return 'Select a contact by name';
      },
    },
    label: {
      type: String,
      required: false,
      default: () => {
        return 'Contact';
      },
    },
    size: {
      type: String,
      required: false,
      default: () => {
        return 'small';
      },
    },
    requireEmail: {
      type: Boolean,
      required: false,
      default: () => {
        return undefined;
      },
    },
    block: {
      type: Boolean,
      required: false,
    },
    useVuetify: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
    error: {
      type: Boolean,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: undefined,
      searchQuery: undefined,
      options: [],
      loading: false,
      internalValue: undefined,
      showResults: false,
      selectedContact: null,
      debounceTimeout: null,
    };
  },
  mounted() {
    if (this.value) {
      this.loading = true;
      this.fetchContact(this.value).then((c) => {
        this.options.push(c);
        this.internalValue = c.id;
        this.loading = false;

        if (this.useVuetify) {
          this.searchQuery = c.first_name + ' ' + c.last_name;
        }
      });
    }
    if (this.useVuetify) {
      document.addEventListener('click', this.handleClickOutside);
    }
  },
  beforeUnmount() {
    if (this.useVuetify) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  },
  watch: {
    value: {
      handler(id) {
        this.internalValue = id;
        if (id == undefined) {
          this.selectedContact = null;
          if (this.useVuetify) {
            this.searchQuery = '';
          }
          return;
        }
        if (!this.options.find((c) => c.id == id)) {
          this.fetchContact(id).then((c) => {
            this.options.push(c);
            this.selectedContact = c;

            if (this.useVuetify) {
              this.searchQuery = c.first_name + ' ' + c.last_name;
            }
          });
        } else {
          this.selectedContact = this.options.find((c) => c.id == id);

          if (this.useVuetify) {
            this.searchQuery =
              this.selectedContact.first_name +
              ' ' +
              this.selectedContact.last_name;
          }
        }
      },
    },
  },
  methods: {
    ...mapActions('contacts', ['fetchContact']),
    doChange(event) {
      this.$emit('update:value', this.internalValue);
      this.search = undefined;
      this.internalValue = undefined;
    },
    handleClickOutside(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.showResults = false;
      }
    },
    onSearchInput() {
      // Show results panel when typing
      this.showResults = true;

      // Clear previous timeout
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      // Clear selection if user changes the input
      if (this.selectedContact) {
        const fullName =
          this.selectedContact.first_name +
          ' ' +
          this.selectedContact.last_name;
        if (this.searchQuery !== fullName) {
          this.selectedContact = null;
          this.internalValue = undefined;
          this.$emit('update:value', undefined);
        }
      }

      // Debounce the search
      this.debounceTimeout = setTimeout(() => {
        this.filterContacts(this.searchQuery);
      }, 300);
    },
    selectContact(contact) {
      this.selectedContact = contact;
      this.internalValue = contact.id;
      this.searchQuery = contact.first_name + ' ' + contact.last_name;
      this.showResults = false;
      this.$emit('update:value', contact.id);
    },
    async filterContacts(search) {
      if (!search || search.length < 2) {
        this.options = [];
        this.loading = false;
        return;
      }

      this.loading = true;

      let filters = [];
      let tokens = search.split(' ');
      let email = undefined;
      // Remove an email token if there is one
      let emailToken = tokens.find((t) => t.includes('@'));
      if (emailToken) {
        tokens = tokens.filter((t) => t != emailToken);
        email = emailToken;
      }
      // If we have 2 tokens left after email assume its names in order
      if (tokens.length >= 2) {
        const fname = tokens[0].trim();
        const lname = tokens[1].trim();
        if (fname) {
          filters.push(`first_name:ilike:%${fname}%:and`);
        }
        if (lname) filters.push(`last_name:ilike:%${lname}%:and`);
      } else if (tokens.length == 1) {
        const fname = tokens[0].trim();
        filters.push(`first_name:ilike:%${fname}%:or`);
        filters.push(`last_name:ilike:%${fname}%:or`);
      }

      const result = await ContactService.getContacts(
        0,
        filters,
        undefined,
        10,
        undefined,
        undefined,
        email,
        this.requireEmail,
        undefined
      );
      this.options = result.data.data;
      this.loading = false;
    },
    formatPhoneNumber(phone) {
      // Should we format it ???
      return phone;
    },
  },
};
</script>
<style lang="scss" scoped>
.typeahead-container {
  position: relative;
  width: 100%;
}

.results-dropdown {
  position: absolute;
  top: 100%; /* Position right below the input */
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ddd;
  border-top: none; /* Remove top border to connect with input */
  border-radius: 0 0 8px 8px;
  z-index: 1015;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: -1px; /* Adjust for perfect alignment */
}

.result-item {
  padding: 10px 15px;
  cursor: pointer;
  text-align: left;
}

.result-item:hover {
  background-color: #f5f7fa;
}

.contact-name {
  font-weight: 500;
}

.contact-email {
  color: #909399;
  margin-top: 2px;
}

.contact-phone {
  color: #606266;
  margin-top: 2px;
}
</style>
